export type ResultSet = {
  id: string;
  label: string;
  searchIds: string[];
  widget: any;
};

export type ResultsState = {
  sets: ResultSet[];
  selectedId: string | null;
};

function itineraries(widget: any, key?: "prior_itineraries" | "next_best_itineraries") {
  if (key) return Array.isArray(widget?.[key]) ? widget[key] : [];
  return [
    ...(Array.isArray(widget?.prior_itineraries) ? widget.prior_itineraries : []),
    ...(Array.isArray(widget?.next_best_itineraries) ? widget.next_best_itineraries : []),
  ];
}

function widgetSearchId(widget: any) {
  try {
    const displayUrl = new URL(widget?.full_display_url, "http://localhost");
    const token = displayUrl.pathname.split("/").filter(Boolean).pop();
    if (token) return token;
  } catch {
    // Fall through to the booking URL.
  }

  try {
    return new URL(widget?.base_booking_url, "http://localhost").searchParams.get("token");
  } catch {
    return null;
  }
}

function widgetOfferIds(widget: any, key?: "prior_itineraries" | "next_best_itineraries") {
  return new Set(itineraries(widget, key).map((itinerary: any) => itinerary?.offer_id).filter(Boolean));
}

function formatSearchDate(value: any) {
  if (typeof value !== "string" || !value) return null;
  const date = new Date(`${value.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(date);
}

function resultSetLabel(widget: any, index: number) {
  const itinerary = itineraries(widget)[0];
  if (!itinerary) return `Search ${index + 1}`;

  const route = itinerary.fromAirport && itinerary.toAirport
    ? `${itinerary.fromAirport} → ${itinerary.toAirport}`
    : null;
  const outboundDate = formatSearchDate(itinerary.outboundTrip?.flights?.[0]?.leavingAt);
  const returnDate = formatSearchDate(itinerary.returnTrip?.flights?.[0]?.leavingAt);
  const dates = outboundDate ? `${outboundDate}${returnDate ? `–${returnDate}` : ""}` : null;

  return [route, dates].filter(Boolean).join(" · ") || `Search ${index + 1}`;
}

export function integrateWidget(state: ResultsState, widget: any): ResultsState {
  const searchId = widgetSearchId(widget);
  let setIndex = searchId
    ? state.sets.findIndex((resultSet) => resultSet.searchIds.includes(searchId))
    : -1;

  if (setIndex < 0) {
    const priorOfferIds = widgetOfferIds(widget, "prior_itineraries");
    if (priorOfferIds.size) {
      setIndex = state.sets.findIndex((resultSet) => {
        const existingOfferIds = widgetOfferIds(resultSet.widget);
        return [...priorOfferIds].some((offerId) => existingOfferIds.has(offerId));
      });
    }
  }

  if (setIndex >= 0) {
    const sets = [...state.sets];
    const existing = sets[setIndex];
    sets[setIndex] = {
      ...existing,
      searchIds: searchId && !existing.searchIds.includes(searchId)
        ? [...existing.searchIds, searchId]
        : existing.searchIds,
      widget: {
        ...widget,
        extra_info: {
          ...existing.widget?.extra_info,
          ...widget?.extra_info,
        },
      },
    };
    return { sets, selectedId: existing.id };
  }

  const id = searchId || `result-set-${state.sets.length + 1}`;
  const resultSet = {
    id,
    label: resultSetLabel(widget, state.sets.length),
    searchIds: searchId ? [searchId] : [],
    widget,
  };
  return { sets: [...state.sets, resultSet], selectedId: id };
}

export function buildResultsState(widgets: any[]) {
  return widgets.reduce<ResultsState>(
    (state, widget) => integrateWidget(state, widget),
    { sets: [], selectedId: null },
  );
}

export function itineraryCount(widget: any) {
  return itineraries(widget).length;
}
